/**
 * ============================================================
 * KEJJA OS — src/hooks/useCalendar.ts
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  calendarApi,
  type CalendarCreatePayload, type CalendarUpdatePayload,
  type EventCreatePayload, type EventUpdatePayload,
} from '@/lib/calendarApi';

const invalidate = (qc: ReturnType<typeof useQueryClient>, homeId: string) => {
  qc.invalidateQueries({ queryKey: ['calendar', homeId] });
};

// ============================================================
// Pulse
// ============================================================

export function useCalendarPulse(homeId: string | undefined) {
  return useQuery({
    queryKey: ['calendar', homeId, 'pulse'],
    queryFn: () => calendarApi.pulse(homeId!),
    enabled: Boolean(homeId),
    staleTime: 30 * 1000,
  });
}

// ============================================================
// Calendars
// ============================================================

export function useCalendars(homeId: string | undefined) {
  return useQuery({
    queryKey: ['calendar', homeId, 'calendars'],
    queryFn: () => calendarApi.listCalendars(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useCreateCalendar(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CalendarCreatePayload) => calendarApi.createCalendar(homeId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useUpdateCalendar(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ calendarId, patch }: { calendarId: string; patch: CalendarUpdatePayload }) =>
      calendarApi.updateCalendar(homeId!, calendarId, patch),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeleteCalendar(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (calendarId: string) => calendarApi.deleteCalendar(homeId!, calendarId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

// ============================================================
// Events
// ============================================================

export function useEvents(homeId: string | undefined, params: {
  from: string; to: string; calendarIds?: string[]; includeRoutines?: boolean;
} | null) {
  return useQuery({
    queryKey: ['calendar', homeId, 'events', params?.from, params?.to, params?.calendarIds?.join(','), params?.includeRoutines],
    queryFn: () => calendarApi.listEvents(homeId!, params!),
    enabled: Boolean(homeId) && Boolean(params),
  });
}

export function useEvent(homeId: string | undefined, eventId: string | undefined) {
  return useQuery({
    queryKey: ['calendar', homeId, 'event', eventId],
    queryFn: () => calendarApi.getEvent(homeId!, eventId!),
    enabled: Boolean(homeId) && Boolean(eventId),
  });
}

export function useCreateEvent(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EventCreatePayload) => calendarApi.createEvent(homeId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useUpdateEvent(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, patch }: { eventId: string; patch: EventUpdatePayload }) =>
      calendarApi.updateEvent(homeId!, eventId, patch),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeleteEvent(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => calendarApi.deleteEvent(homeId!, eventId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}
